import { useEffect } from 'react';
import { CreateTicketFormData, CreateTicketPayload } from '../Tickets/types';
import {
  useForm,
  Select,
  TextField,
  TextArea,
  MultiSelect,
  withStyles,
  MaterialSymbol,
  UploadFile,
  TicketCategoryID,
  FieldData,
} from 'vopay';
import useFetchTicketCategoryTypes from '@/hooks/useApi/useFetchTicketCategoryTypes';
import { useTranslation } from 'react-i18next';
import * as Yup from 'yup';
import styles from './CreateTicketDrawer.module.scss';
import useAccountUsers from '@/hooks/useApi/useAccountUsers';
import { useUserSettingsProvider } from '@/providers/UserSettings.provider';

const cn = withStyles(styles);

type UseCreateTicketConfigProps = {
  onSubmit: (payload: CreateTicketPayload) => void;
};

export function useCreateTicketConfig({ onSubmit }: UseCreateTicketConfigProps) {
  const { t } = useTranslation('translations', { keyPrefix: 'CreateTicketDrawer' });
  const { ticketCategories, ticketSubCategories, fetchTicketCategoryTypes, fetching } = useFetchTicketCategoryTypes();
  const { users, fetchUsers } = useAccountUsers();
  const { userSettings } = useUserSettingsProvider();

  useEffect(() => {
    fetchUsers({
      Timezone: userSettings.timezone,
    });
  }, []);

  const detailsRows: FieldData<CreateTicketFormData>[] = [
    {
      label: () => t('category'),
      fieldName: 'TicketCategory',
      getInputState: () => 'required',
      showField: () => true,
      renderField: ({ getFormikData, ctx }) => {
        const fieldData = getFormikData<string>({
          onChange: (e) => {
            const firstSubCategory = ticketSubCategories.find(
              (item) => item.SupportTicketCatagories_ID === e.target.value,
            );
            ctx.setFieldValue('TicketSubCategory', firstSubCategory?.SubCategory);
          },
        });
        return (
          <Select
            version="2"
            showClear
            sort={false}
            loading={fetching}
            options={ticketCategories}
            optionLabel={(o) => o.Category}
            optionValue={(o) => o.ID}
            {...fieldData}
          />
        );
      },
      validate: () => Yup.string().required(),
    },
    {
      label: () => t('subCategory'),
      fieldName: 'TicketSubCategory',
      getInputState: () => 'required',
      showField: (values) => values.TicketCategory !== TicketCategoryID.GENERAL_INQUIRY,
      renderField: ({ getFormikData, ctx }) => {
        const fieldData = getFormikData<string>({});
        return (
          <Select
            version="2"
            showClear
            sort={false}
            loading={fetching}
            options={ticketSubCategories.filter(
              (item) => item.SupportTicketCatagories_ID === ctx.values.TicketCategory,
            )}
            optionLabel={(o) => o.SubCategory}
            optionValue={(o) => o.SubCategory}
            {...fieldData}
          />
        );
      },
      validate: () => Yup.string().required(),
    },
    {
      label: () => t('transactionID'),
      fieldName: 'TransactionID',
      getInputState: () => 'optional',
      showField: (values) => values.TicketCategory === '2',
      renderField: ({ getFormikData }) => {
        const fieldData = getFormikData<string>({});
        return <TextField variant="outlineShadow" placeholder={t('transactionIDPlaceholder')} {...fieldData} />;
      },
    },
    {
      label: () => t('subject'),
      fieldName: 'TicketSubject',
      getInputState: () => 'required',
      showField: () => true,
      renderField: ({ getFormikData }) => {
        const fieldData = getFormikData<string>({});
        return <TextField variant="outlineShadow" placeholder={t('ticketSubjectPlaceholder')} {...fieldData} />;
      },
      validate: () => Yup.string().required(),
    },
    {
      label: () => t('description'),
      fieldName: 'TicketContent',
      getInputState: () => 'required',
      showField: () => true,
      renderField: ({ getFormikData }) => {
        const fieldData = getFormikData<string>({});
        return <TextArea height="90px" placeholder={t('descriptionPlaceholder')} {...fieldData} />;
      },
      validate: () => Yup.string().required(),
    },
  ];

  const additionalDetailsConfig: FieldData<CreateTicketFormData>[] = [
    {
      label: () => t('ccEmails'),
      fieldName: 'CcEmails',
      getInputState: () => 'optional',
      showField: () => true,
      serializeField: (values) => values.CcEmails?.join(','),
      renderField: ({ ctx, getFormikData }) => {
        const multiSelectFieldData = getFormikData<string[]>({
          onChange: (e) => {
            const value = e.target.value;
            ctx.setFieldValue('CcEmails', value === '' ? [] : value.split(','));
          },
        });

        return (
          <>
            <MultiSelect
              renderSelectedValue={(_, { Placeholder }) => <Placeholder />}
              placeholder={t('ccEmailsPlaceholder')}
              filterable
              options={users}
              optionValue={(o) => o.Username}
              optionLabel={(o) => o.Username}
              {...multiSelectFieldData}
            />

            <p className={cn('multiselectDescription')}>{t('ccEmailsDescription')}</p>

            {!!ctx.values.CcEmails?.length && (
              <div className={cn('multiselectOptionsContainer')}>
                {ctx.values.CcEmails.map((m) => (
                  <div key={m} className={cn('multiselectOption')}>
                    <span>{m}</span>

                    <div
                      style={{ cursor: 'pointer' }}
                      onClick={() => {
                        const filteredList = ctx.values.CcEmails.filter((a) => {
                          return m !== a;
                        });

                        ctx.setFieldValue('CcEmails', filteredList);
                      }}
                    >
                      <MaterialSymbol icon="close" />
                    </div>
                  </div>
                ))}
              </div>
            )}
          </>
        );
      },
    },
    {
      label: () => t('attachment'),
      fieldName: 'TicketAttachment',
      getInputState: () => 'optional',
      showField: () => true,
      renderField: ({ getFormikData, ctx }) => {
        const fieldData = getFormikData<string>({});
        return (
          <UploadFile
            id={fieldData.name}
            handleFileName={(fileName) => ctx.setFieldValue('AttachmentFileName', fileName)}
            variant="onboarding"
            maxMBSize={4}
            validExtensions={['jpg', 'jpeg', 'png', 'pdf']}
            base64
            {...(ctx.values.TicketAttachment ? { valueFileName: ctx.values.AttachmentFileName, height: '30px' } : {})}
            {...fieldData}
          />
        );
      },
    },
  ];

  const fieldDataList: FieldData<CreateTicketFormData>[] = [...detailsRows, ...additionalDetailsConfig];

  useEffect(() => {
    fetchTicketCategoryTypes();
  }, []);

  const initialValues: CreateTicketFormData = {
    CcEmails: [],
    TicketCategory: TicketCategoryID.GENERAL_INQUIRY,
    TicketSubCategory: 'General inquiry',
    AttachmentFileName: '',
    TicketSubject: '',
    AccountName: '',
    TicketContent: '',
    SenderEmailAddress: '',
    TicketRequesterName: '',
    TicketAttachment: '',
    TransactionID: '',
  };

  const { renderFormElements, ctx } = useForm<CreateTicketFormData, CreateTicketPayload>({
    config: [
      ...fieldDataList,
      {
        label: () => 'AttachmentFileName',
        fieldName: 'AttachmentFileName',
        getInputState: () => 'optional',
        showField: () => true,
        renderField: () => null,
      },
    ],
    initialValues,
    onSubmit: (serializedData) => {
      onSubmit({
        ...serializedData,
        TicketSubCategory: serializedData.TicketSubCategory || initialValues.TicketSubCategory,
      });
    },
  });

  return {
    ctx,
    renderFormElements,
    detailsRows,
    frequencyRows: additionalDetailsConfig,
  };
}
